/**
 * Connected "The Climb" screen (specs/rank-ladder). Reads the XP summary, builds the
 * scene model, and renders RankLadderView. Presented as a pushed route on top of the
 * current screen; the back control returns the player to where they were. The app tab
 * bar is hidden while the climb is focused so it reads as a takeover.
 */

import React, { useCallback, useMemo } from 'react';
import { useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { getLayoutStyles } from '../../../../appStyles/_layout.styles';
import { useTheme } from '../../../theme/useTheme';
import { useXpSummary } from '../../hooks/useXpSummary';
import { useSelfRank } from '../../hooks/useSelfRank';
import { buildRankLadderScene } from './rankLadder.helper';
import { RankLadderView } from './RankLadderView';

export const RankLadderScreen: React.FC = () => {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const theme = useTheme();
  const navigation = useNavigation();
  // Rank + coin progress are coin-based (specs/coin-based-ranks); level is XP flavour on the camp.
  const { rankIndex, effectivePeak } = useSelfRank();
  const { data } = useXpSummary();

  const nodes = useMemo(
    () =>
      buildRankLadderScene(
        rankIndex != null
          ? { rankIndex, level: data?.level ?? 1, peakCoins: effectivePeak }
          : null,
      ),
    [rankIndex, effectivePeak, data?.level],
  );

  const appTabBarStyle = useMemo(
    () => getLayoutStyles(theme, insets.bottom).tabBar,
    [theme, insets.bottom],
  );

  // Hide the app tab bar while the climb is focused; restore the themed bar on blur.
  useFocusEffect(
    useCallback(() => {
      navigation.setOptions({ tabBarStyle: { display: 'none' } });
      return () => navigation.setOptions({ tabBarStyle: appTabBarStyle });
    }, [navigation, appTabBarStyle]),
  );

  // Mirror the hidden-tab-screen dismissal pattern (components/league/LeagueDetailView):
  // return to the origin screen via history, falling back to a home tab if there is none
  // (e.g. reached without a back stack) so the player is never stranded on the climb.
  const handleBack = useCallback(() => {
    if (router.canGoBack()) {
      router.back();
      return;
    }
    router.replace('/app/arena');
  }, [router]);

  return <RankLadderView nodes={nodes} onBack={handleBack} safeAreaTop={insets.top} />;
};
